/**
 * Routes. The original used hash URLs (#/project/<id>); this template uses real paths.
 * Old hash links are rewritten by redirectLegacyHash() at start-up so bookmarks keep working.
 */
import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { LoginPage } from '@/auth/LoginPage';
import { LoadingState, Topbar } from '@/ui/kit';
import { AppLayout } from './AppLayout';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ProjectsPage } from '@/features/projects/ProjectsPage';
import { ProjectDetailPage } from '@/features/projects/ProjectDetailPage';
import { MembersPage } from '@/features/members/MembersPage';
import { MemberDetailPage } from '@/features/members/MemberDetailPage';
import { WorkLogPage } from '@/features/worklog/WorkLogPage';
import { DailyReportPage } from '@/features/report/DailyReportPage';
import { SettingsPage } from '@/features/settings/SettingsPage';

// Charts are only needed here, so Chart.js loads with this page.
const ManagementPage = lazy(() => import('@/features/management/ManagementPage').then((m) => ({ default: m.ManagementPage })));

const LEGACY: Record<string, string> = { dashboard: '/dashboard', projects: '/projects', project: '/projects', members: '/members', member: '/members', worklog: '/work-log', report: '/daily-report', management: '/management', settings: '/settings' };

/** `/#/project/abc?t=links` → `/projects/abc?t=links` */
export function legacyHashTarget(hash: string): string | null {
  if (!hash.startsWith('#/')) return null;
  const [path, qs] = hash.slice(2).split('?');
  const [view, id] = path.split('/');
  const base = LEGACY[view];
  if (!base) return null;
  return base + (id ? '/' + id : '') + (qs ? '?' + qs : '');
}

/** Call once before the router starts: rewrites an old `#/…` bookmark to its path. */
export function redirectLegacyHash() {
  const t = legacyHashTarget(window.location.hash);
  if (t) window.history.replaceState(null, '', t);
}

function PageNotFound() {
  return (
    <>
      <Topbar title="Page not found" />
      <div className="panel">
        <div className="empty">No records found.</div>
      </div>
    </>
  );
}

const guard = (el: React.ReactNode, anyOf?: Parameters<typeof ProtectedRoute>[0]['anyOf']) => <ProtectedRoute anyOf={anyOf}>{el}</ProtectedRoute>;

export const routes: RouteObject[] = [
  // Log In (API mode) — outside the sidebar layout. In demo mode it redirects to the Dashboard.
  { path: 'login', element: <LoginPage /> },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: guard(<DashboardPage />) },
      { path: 'projects', element: guard(<ProjectsPage />) },
      { path: 'projects/:id', element: guard(<ProjectDetailPage />) },
      { path: 'members', element: guard(<MembersPage />) },
      { path: 'members/:id', element: guard(<MemberDetailPage />) },
      { path: 'work-log', element: guard(<WorkLogPage />) },
      { path: 'daily-report', element: guard(<DailyReportPage />) },
      {
        path: 'management',
        element: guard(
          <Suspense fallback={<LoadingState label="Loading the Management View…" />}>
            <ManagementPage />
          </Suspense>,
          ['management:view'],
        ),
      },
      { path: 'settings', element: guard(<SettingsPage />, ['settings:write', 'backup:manage']) },
      { path: '*', element: guard(<PageNotFound />) },
    ],
  },
];
